<template>
	<Dialog
		modal
		:visible="visible"
		@update:visible="emit('update:visible', $event)"
		@hide="handleHide"
		:dismissable-mask="dismissableMask"
		:style="modalStyle"
		pt:root:class="!border-0 !bg-transparent"
	>
		<template #container="{ closeCallback }">
			<div class="flex flex-col gap-3 rounded-2xl drop-shadow-2xl box" :class="{ 'gradient-background': useGradientBackground }">
				<div v-if="showHeader" class="text-xl rounded-2xl rounded-b-none px-4 py-3 header" :class="{ 'custom-header-style': useCustomHeaderFooter }">
					<slot name="header">{{ header }}</slot>
				</div>
				<div class="px-4 py-3">
					<slot name="content" :close="closeCallback"></slot>
				</div>
				<div v-if="showFooter" class="flex items-end justify-end gap-2 px-4 py-3 rounded-b-2xl footer" :class="{ 'custom-footer-style': useCustomHeaderFooter }">
					<slot name="footer" :close="closeCallback">
						<Button
							v-if="showCancelButton"
							:label="cancelLabel"
							severity="secondary"
							@click="handleCancel(closeCallback)"
							class="min-w-36"
						></Button>
						<Button
							v-if="showOkButton"
							:label="okLabel"
							severity="primary"
							@click="handleOk(closeCallback)"
							class="min-w-36"
						></Button>
					</slot>
				</div>
			</div>
		</template>
	</Dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

interface Props {
	visible: boolean
	header?: string
	width?: string
	dismissableMask?: boolean
	showHeader?: boolean
	showFooter?: boolean
	showCancelButton?: boolean
	showOkButton?: boolean
	cancelLabel?: string
	okLabel?: string
	useGradientBackground?: boolean
	useCustomHeaderFooter?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	width: '500px',
	dismissableMask: true,
	showHeader: true,
	showFooter: true,
	showCancelButton: false,
	showOkButton: true,
	useGradientBackground: false,
	useCustomHeaderFooter: false
})

const emit = defineEmits<{
	'update:visible': [value: boolean]
	hide: []
	cancel: []
	ok: []
}>()

const { t } = useI18n()

const modalStyle = computed(() => ({
	width: props.width
}))

const cancelLabel = computed(() => props.cancelLabel || t('common.cancel'))
const okLabel = computed(() => props.okLabel || t('common.ok'))

const handleHide = () => {
	emit('update:visible', false)
	emit('hide')
}

const handleCancel = (closeCallback: () => void) => {
	emit('cancel')
	closeCallback()
}

const handleOk = (closeCallback: () => void) => {
	emit('ok')
	closeCallback()
}
</script>

<style scoped>
.box {
	background-color: var(--p-content-background);
	color: var(--p-content-color);
}

.box.gradient-background {
	background-image: var(--modal-background);
}

.header {
	background-color: #00000020;
	color: var(--p-primary-700);
}

.header.custom-header-style {
	background-color: transparent;
	background-image: var(--dialog-header-background);
	color: var(--p-text-color);
}

.footer {
	border-top: 1px solid var(--p-primary-100);
}

.footer.custom-footer-style {
	background-color: transparent;
	background-image: var(--dialog-header-background);
	border-top: 1px solid #00000018;
}

@media (prefers-color-scheme: dark) {
	.header {
		background-color: #FFFFFF10;
		color: var(--p-primary-200);
	}
	.footer {
		border-top: 1px solid var(--p-primary-800);
	}
	.footer.custom-footer-style {
		border-top: 1px solid #ffffff20;
	}
}
</style>
